import type { IDTRole } from '@seen/core/models/care-teams/constants'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import type { QueryTypes } from '@seen/core/search'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

interface InternalPractitionersQueryOptions {
  /* Notes stay on the chart after someone is deactivated. Role filters need
   * those owners; assignment pickers do not. */
  includeInactive?: boolean
}

function withoutActiveFilter(query: QueryTypes): QueryTypes {
  const { active: _active, ...rest } = query
  return rest
}

function internalPractitionersQuery(
  idtRoles: readonly IDTRole[],
  options?: InternalPractitionersQueryOptions
) {
  if (options?.includeInactive && idtRoles.length === 0) {
    return ExtendedPractitioner.allInternalIncludingInactiveSearchQuery()
  }
  const query =
    idtRoles.length > 0
      ? ExtendedPractitioner.queryByIDTRoles(idtRoles)
      : ExtendedPractitioner.allInternalSearchQuery()
  return options?.includeInactive ? withoutActiveFilter(query) : query
}

export function usePractitionersInternal(
  idtRoles: readonly IDTRole[] = [],
  options?: InternalPractitionersQueryOptions
): ExtendedPractitioner[] {
  const { data: practitioners } = useSearchResourcePages(
    ExtendedPractitioner,
    internalPractitionersQuery(idtRoles, options)
  )

  return practitioners ?? []
}

interface UsePractitionersInternalQueryResult {
  practitioners: ExtendedPractitioner[]
  isLoading: boolean
}

// Same query as usePractitionersInternal, but exposes loading state for callers
// (e.g. the practitioner table) that need to distinguish "loading" from "empty".
export function usePractitionersInternalQuery(
  idtRoles: readonly IDTRole[] = [],
  options?: InternalPractitionersQueryOptions
): UsePractitionersInternalQueryResult {
  const { data: practitioners, isLoading } = useSearchResourcePages(
    ExtendedPractitioner,
    internalPractitionersQuery(idtRoles, options)
  )
  return { practitioners: practitioners ?? [], isLoading }
}
